import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Calendar, Clock, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';

const BlogPage = () => {
  const blogPosts = [
    {
      id: 1,
      title: "10 Essential Spring Cleaning Tips for a Fresh Home",
      excerpt: "Discover our professional tips to make your spring cleaning efficient and thorough. From decluttering strategies to deep cleaning techniques.",
      category: "Cleaning Tips",
      date: "March 15, 2025",
      readTime: "5 min read",
      image: "https://images.pexels.com/photos/6196694/pexels-photo-6196694.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
    },
    {
      id: 2,
      title: "The Benefits of Professional Commercial Cleaning",
      excerpt: "Learn why investing in professional cleaning services can boost productivity, improve employee health, and enhance your business image.",
      category: "Business",
      date: "March 10, 2025",
      readTime: "4 min read",
      image: "https://images.pexels.com/photos/6195129/pexels-photo-6195129.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
    },
    {
      id: 3,
      title: "Eco-Friendly Cleaning: Better for You and the Environment",
      excerpt: "Explore the benefits of eco-friendly cleaning products and how they create a healthier home without compromising on cleanliness.",
      category: "Sustainability",
      date: "March 5, 2025",
      readTime: "6 min read",
      image: "https://images.pexels.com/photos/5591926/pexels-photo-5591926.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
    },
    {
      id: 4,
      title: "How to Prepare Your Home for Professional Cleaners",
      excerpt: "Make the most of your professional cleaning service with our guide on how to prepare your space for maximum efficiency.",
      category: "Guides",
      date: "February 28, 2025",
      readTime: "3 min read",
      image: "https://images.pexels.com/photos/4239037/pexels-photo-4239037.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
    },
    {
      id: 5,
      title: "Post-Construction Cleaning: What You Need to Know",
      excerpt: "Everything you need to know about post-construction cleaning, from dust removal to final detailing for move-in readiness.",
      category: "Specialized Services",
      date: "February 20, 2025",
      readTime: "7 min read",
      image: "https://images.pexels.com/photos/6195114/pexels-photo-6195114.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
    },
    {
      id: 6,
      title: "Maintaining a Spotless Air BnB: Host's Guide",
      excerpt: "Learn the secrets to keeping your short-term rental pristine and earning 5-star cleanliness reviews from every guest.",
      category: "Air BnB",
      date: "February 15, 2025",
      readTime: "5 min read",
      image: "https://images.unsplash.com/photo-1758273238415-01ec03d9ef27"
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <section className="bg-black text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-4xl md:text-5xl font-bold mb-4">
              Cleaning Tips & <span className="text-red-600">Insights</span>
            </h1>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              Expert advice, industry insights, and cleaning tips from our 40+ years of experience
            </p>
          </div>
        </div>
      </section>

      {/* Blog Posts Grid */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {blogPosts.map((post) => (
              <Card key={post.id} className="overflow-hidden hover:shadow-xl transition-shadow">
                <div className="h-48 overflow-hidden">
                  <img 
                    src={post.image} 
                    alt={post.title}
                    className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                  />
                </div>
                <CardHeader>
                  <div className="flex items-center justify-between mb-2">
                    <Badge variant="secondary">{post.category}</Badge>
                    <div className="flex items-center text-sm text-gray-500">
                      <Clock className="w-4 h-4 mr-1" />
                      {post.readTime}
                    </div>
                  </div>
                  <CardTitle className="text-xl hover:text-red-600 transition-colors">
                    {post.title}
                  </CardTitle>
                  <CardDescription className="flex items-center text-sm text-gray-500 mt-2">
                    <Calendar className="w-4 h-4 mr-1" />
                    {post.date}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-700 mb-4">{post.excerpt}</p>
                  <button className="text-red-600 font-medium flex items-center hover:text-red-700 transition-colors">
                    Read More
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </button>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Newsletter CTA */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold mb-4">Stay Updated</h2>
          <p className="text-gray-600 mb-8">
            Subscribe to our newsletter for the latest cleaning tips, special offers, and industry insights
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center max-w-md mx-auto">
            <input 
              type="email" 
              placeholder="Your email address"
              className="flex-1 px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-600"
            />
            <button className="px-6 py-3 bg-red-600 hover:bg-red-700 text-white font-medium rounded-md transition-colors">
              Subscribe
            </button>
          </div>
          <p className="text-sm text-gray-500 mt-4">
            No spam, unsubscribe anytime
          </p>
        </div>
      </section>
    </div>
  );
};

export default BlogPage;
